import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ClipboardList } from "lucide-react";

const FIELD_DEFS = {
  name: { label: "Nom complet", labelKey: "adlead.name", type: "text", placeholder: "Votre nom", phKey: "adlead.name_ph" },
  email: { label: "E-mail", labelKey: "adlead.email", type: "email", placeholder: "vous@exemple.com" },
  phone: { label: "Téléphone", labelKey: "adlead.phone", type: "tel", placeholder: "+32 ..." },
  message: { label: "Message", labelKey: "adlead.message", type: "textarea", placeholder: "Décrivez votre besoin...", phKey: "adlead.message_ph" },
};

// Native lead form shown when an ad's CTA type is "lead_form".
export const AdLeadDialog = ({ ad, title, open, onOpenChange }) => {
  const { t } = useApp();
  const fields = (ad?.lead_fields && ad.lead_fields.length ? ad.lead_fields : ["name", "phone"]);
  const customFields = ad?.custom_fields || [];
  const [vals, setVals] = useState({});
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setVals((s) => ({ ...s, [k]: v }));
  const setAnswer = (id, v) => setAnswers((s) => ({ ...s, [id]: v }));

  const submit = async () => {
    for (const f of customFields) {
      if (f.required && !String(answers[f.id] || "").trim()) {
        toast.info(t("adlead.required", "Le champ « {f} » est requis.").replace("{f}", f.label));
        return;
      }
    }
    const hasContact = ["name", "email", "phone"].some((k) => (vals[k] || "").trim());
    const hasAnswer = customFields.some((f) => String(answers[f.id] || "").trim());
    if (!hasContact && !hasAnswer) { toast.info(t("adlead.at_least_one", "Renseignez au moins un champ.")); return; }
    setLoading(true);
    try {
      await api.post(`/ads/${ad.id}/lead`, {
        name: vals.name || "", email: vals.email || "", phone: vals.phone || "", message: vals.message || "",
        answers,
      });
      setDone(true);
      toast.success(t("adlead.sent_toast", "Merci ! Vos coordonnées ont été envoyées."));
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setLoading(false); }
  };

  const close = (v) => { onOpenChange(v); if (!v) { setDone(false); setVals({}); setAnswers({}); } };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent variant="sheet" className="sm:max-w-md" data-testid={`ad-lead-dialog-${ad?.id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <span className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><ClipboardList size={18} /></span>
            {ad?.headline || title || t("adlead.title", "Demander plus d'informations")}
          </DialogTitle>
          <DialogDescription>{ad?.description || t("adlead.hint", "Laissez vos coordonnées, l'annonceur vous recontactera.")}</DialogDescription>
        </DialogHeader>
        <DialogBody>
        {done ? (
          <div className="py-6 text-center" data-testid="ad-lead-success">
            <div className="text-4xl mb-2">✅</div>
            <p className="font-semibold">{t("adlead.sent_title", "Demande envoyée")}</p>
            <p className="text-sm mt-1" style={{ color: "var(--yalola-text-muted)" }}>{t("adlead.sent_body", "L'annonceur a bien reçu vos coordonnées.")}</p>
            <Button className="pill pill-primary mt-4" onClick={() => close(false)} data-testid="ad-lead-close">{t("adlead.close", "Fermer")}</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {fields.map((k) => {
              const d0 = FIELD_DEFS[k];
              const d = { ...d0, label: d0.labelKey ? t(d0.labelKey, d0.label) : d0.label, placeholder: d0.phKey ? t(d0.phKey, d0.placeholder) : d0.placeholder };
              if (!d) return null;
              return (
                <div key={k}>
                  <Label>{d.label}</Label>
                  {d.type === "textarea" ? (
                    <Textarea rows={3} value={vals[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={d.placeholder} data-testid={`ad-lead-${k}`} />
                  ) : (
                    <Input type={d.type} value={vals[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={d.placeholder} data-testid={`ad-lead-${k}`} />
                  )}
                </div>
              );
            })}
            {customFields.map((f) => (
              <div key={f.id}>
                <Label>{f.label}{f.required ? " *" : ""}</Label>
                {f.type === "select" ? (
                  <select
                    value={answers[f.id] || ""}
                    onChange={(e) => setAnswer(f.id, e.target.value)}
                    className="w-full h-10 rounded-xl border px-3 text-sm bg-white"
                    style={{ borderColor: "var(--yalola-border)" }}
                    data-testid={`ad-lead-custom-${f.id}`}
                  >
                    <option value="">{t("adlead.choose", "Choisir…")}</option>
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <Input
                    type={f.type === "number" ? "number" : "text"}
                    value={answers[f.id] || ""}
                    onChange={(e) => setAnswer(f.id, e.target.value)}
                    data-testid={`ad-lead-custom-${f.id}`}
                  />
                )}
              </div>
            ))}
          </div>
        )}
        </DialogBody>
        {!done && (
          <DialogFooter>
            <Button className="pill pill-primary w-full justify-center" onClick={submit} disabled={loading} data-testid="ad-lead-submit">
              {loading ? t("adlead.sending", "Envoi...") : t("adlead.submit", "Envoyer ma demande")}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdLeadDialog;
